import { CheckSquare, FileText } from 'lucide-react'
import { Link } from 'react-router-dom'

// Backend ile AYNI küme (chat_service.MENTION_ALL_TOKENS) — ayrışırsa etiket
// bildirim gönderir ama ekranda düz kişi etiketi gibi görünür.
const MENTION_ALL = new Set(['herkes', 'kanal', 'everyone', 'channel', 'all'])


/** Uygulama içi bağlantı — SPA navigasyonu + tıklama mesaj satırına sızmaz */
function RichLink({ to, className, children, title }) {
  return (
    <Link to={to} title={title} className={className}
      onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  )
}

/**
 * Sohbet mesajı zengin metin render — markdown alt kümesi + VERTEX token'ları.
 *
 * Blok: # başlık, - / * liste, 1. numaralı liste, > alıntı, ``` kod bloğu, --- ayırıcı,
 *       - [ ] / - [x] kontrol listesi, | tablo |
 * Satır içi: **kalın**, *italik*, ~~üstü çizili~~, `kod`, ==vurgu==, [metin](url),
 *            @kullanıcı (gerçek ad), @@görevId, düz link
 */

const INLINE_RE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_|~~[^~\n]+~~|==[^=\n]+==|`[^`\n]+`|\[[^\]\n]+\]\((?:https?:\/\/|\/)[^)\s]+\)|@@\d+|@[A-Za-z0-9_.\-]+|https?:\/\/\S+)/g

function renderInline(text, ctx, keyPrefix = 'i') {
  const { taskRefs, mentionRefs, onOpenProfile } = ctx
  const out = []
  let last = 0, m, k = 0
  while ((m = INLINE_RE.exec(text || '')) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    const key = `${keyPrefix}${k++}`
    if (tok.startsWith('**') || tok.startsWith('__')) {
      out.push(<strong key={key} className="font-semibold">{tok.slice(2, -2)}</strong>)
    } else if (tok.startsWith('~~')) {
      out.push(<span key={key} className="line-through text-gray-500">{tok.slice(2, -2)}</span>)
    } else if (tok.startsWith('==')) {
      out.push(<mark key={key} className="bg-amber-100 text-gray-900 rounded px-0.5">{tok.slice(2, -2)}</mark>)
    } else if (tok.startsWith('`')) {
      out.push(<code key={key} className="px-1 py-0.5 rounded bg-gray-100 text-[12.5px] font-mono text-rose-600">{tok.slice(1, -1)}</code>)
    } else if (tok.startsWith('[')) {
      const mm = tok.match(/^\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)$/)
      if (mm && mm[2].startsWith('/')) {
        // VERTEX belge referansı (teklif/sipariş/satınalma/kanal) — uygulama içi, chip görünümü
        out.push(
          <RichLink key={key} to={mm[2]} title={mm[1]}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[12px] font-medium align-middle hover:bg-amber-100 hover:underline">
            <FileText size={11} />{mm[1]}
          </RichLink>)
      } else {
        out.push(<a key={key} href={mm[2]} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{mm[1]}</a>)
      }
    } else if (tok.startsWith('@@')) {
      const id = tok.slice(2)
      const t = taskRefs?.[id]
      out.push(
        <RichLink key={key} to={`/tasks/task/${id}`} title={t?.title || `Görev #${id}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[12px] font-medium align-middle hover:bg-violet-100 hover:underline">
          <CheckSquare size={11} />{t?.title || `Görev #${id}`}
        </RichLink>)
    } else if (tok.startsWith('@')) {
      const uname = tok.slice(1)
      // "@herkes" kanaldaki herkese bildirim gönderen TOPLU etikettir; bir kişiye
      // karşılık gelmediği için profil açmaz ve ayrı renkte gösterilir — normal
      // kişi etiketiyle karışırsa "kime yazdı" belirsizleşir.
      if (MENTION_ALL.has(uname.toLowerCase())) {
        out.push(
          <span key={key} title="Kanaldaki herkese bildirim gider"
            className="text-amber-700 font-semibold bg-amber-50 rounded px-1">@{uname}</span>)
        last = m.index + tok.length
        continue
      }
      const ref = mentionRefs?.[uname]
      out.push(
        <button key={key} onClick={(e) => { e.stopPropagation(); ref && onOpenProfile?.(ref.user_id) }}
          className="text-violet-700 font-medium bg-violet-50 rounded px-0.5 hover:bg-violet-100">
          @{ref?.display_name || uname}
        </button>)
    } else if (tok.startsWith('http')) {
      out.push(<a key={key} href={tok} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{tok}</a>)
    } else {
      // tek yıldız/alt çizgi → italik
      out.push(<em key={key} className="italic">{tok.slice(1, -1)}</em>)
    }
    last = m.index + tok.length
  }
  if (last < (text || '').length) out.push(text.slice(last))
  return out
}

export function renderRichBody(body, taskRefs, mentionRefs, onOpenProfile) {
  const ctx = { taskRefs, mentionRefs, onOpenProfile }
  const lines = String(body || '').split('\n')
  const blocks = []
  let i = 0
  let key = 0

  const flushList = (items, ordered) => {
    const El = ordered ? 'ol' : 'ul'
    blocks.push(
      <El key={`l${key++}`} className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 my-1 space-y-0.5`}>
        {items.map((it, ix) => <li key={ix}>{renderInline(it, ctx, `li${key}_${ix}_`)}</li>)}
      </El>)
  }

  while (i < lines.length) {
    const line = lines[i]

    // kod bloğu ```
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim()
      const buf = []
      i++
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++ }
      i++ // kapanış
      blocks.push(
        <pre key={`c${key++}`} className="my-1.5 px-3 py-2 rounded-lg bg-gray-900 text-gray-100 text-[12.5px] font-mono overflow-x-auto">
          {lang && <div className="text-[10px] text-gray-400 mb-1 uppercase">{lang}</div>}
          <code>{buf.join('\n')}</code>
        </pre>)
      continue
    }

    // ayırıcı
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push(<hr key={`h${key++}`} className="my-2 border-gray-200" />)
      i++
      continue
    }

    // başlık
    const h = line.match(/^(#{1,4})\s+(.*)$/)
    if (h) {
      const lvl = h[1].length
      const cls = ['text-[16px] font-bold', 'text-[15px] font-bold', 'text-[14px] font-semibold', 'text-[13.5px] font-semibold'][lvl - 1]
      blocks.push(<div key={`t${key++}`} className={`${cls} text-gray-900 mt-1.5 mb-0.5`}>{renderInline(h[2], ctx, `h${key}_`)}</div>)
      i++
      continue
    }

    // alıntı
    if (/^>\s?/.test(line)) {
      const buf = []
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, '')); i++ }
      blocks.push(
        <blockquote key={`q${key++}`} className="my-1 pl-3 border-l-2 border-violet-300 text-gray-600 italic">
          {buf.map((b, ix) => <div key={ix}>{renderInline(b, ctx, `q${key}_${ix}_`)}</div>)}
        </blockquote>)
      continue
    }

    // kontrol listesi
    if (/^\s*[-*]\s+\[[ xX]\]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*[-*]\s+\[[ xX]\]\s+/.test(lines[i])) {
        const mm = lines[i].match(/^\s*[-*]\s+\[([ xX])\]\s+(.*)$/)
        items.push({ done: mm[1].toLowerCase() === 'x', text: mm[2] })
        i++
      }
      blocks.push(
        <div key={`k${key++}`} className="my-1 space-y-0.5">
          {items.map((it, ix) => (
            <div key={ix} className="flex items-start gap-1.5">
              <span className={`mt-0.5 w-3.5 h-3.5 rounded border flex items-center justify-center text-[9px] shrink-0
                ${it.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300'}`}>{it.done ? '✓' : ''}</span>
              <span className={it.done ? 'line-through text-gray-400' : ''}>{renderInline(it.text, ctx, `kk${key}_${ix}_`)}</span>
            </div>
          ))}
        </div>)
      continue
    }

    // tablo | a | b |
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const cells = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
      const head = cells(line)
      i += 2
      const rows = []
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { rows.push(cells(lines[i])); i++ }
      blocks.push(
        <div key={`tb${key++}`} className="my-1.5 overflow-x-auto">
          <table className="text-[12.5px] border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>{head.map((c, ix) => <th key={ix} className="px-2 py-1 text-left font-semibold text-gray-700 border-b border-gray-200">{renderInline(c, ctx, `th${key}_${ix}_`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, rx) => (
                <tr key={rx} className="border-b border-gray-100 last:border-0">
                  {r.map((c, cx) => <td key={cx} className="px-2 py-1 text-gray-700">{renderInline(c, ctx, `td${key}_${rx}_${cx}_`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>)
      continue
    }

    // listeler
    if (/^\s*[-*]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i]) && !/^\s*[-*]\s+\[[ xX]\]/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, '')); i++
      }
      flushList(items, false)
      continue
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+[.)]\s+/, '')); i++ }
      flushList(items, true)
      continue
    }

    // düz paragraf (ardışık boş olmayan satırlar tek blokta, satır sonu korunur)
    if (line.trim() === '') {
      blocks.push(<div key={`b${key++}`} className="h-1.5" />)
      i++
      continue
    }
    const buf = []
    while (i < lines.length && lines[i].trim() !== ''
           && !/^(#{1,4}\s|>|```|\s*[-*]\s|\s*\d+[.)]\s|\s*\|)/.test(lines[i])
           && !/^(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i])) {
      buf.push(lines[i]); i++
    }
    if (buf.length) {
      blocks.push(
        <div key={`p${key++}`}>
          {buf.map((b, ix) => <div key={ix}>{renderInline(b, ctx, `p${key}_${ix}_`)}</div>)}
        </div>)
    } else { i++ }
  }
  return blocks
}
